import React, { useState, useEffect, useRef, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';
import {Icon} from 'components/misc';
import UpgradeLink from '../ChatContent/UpgradeLink'

const AudioMessage = () => {
    const waveformRef = useRef(null);
    const [wavesurfer, setWavesurfer] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [hasRecording, setHasRecording] = useState(false);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const [recordingTime, setRecordingTime] = useState(0);
    const intervalRef = useRef(null);

    useEffect(() => {
        const ws = WaveSurfer.create({
            container: waveformRef.current,
            waveColor: 'white',
            progressColor: 'white',
            barWidth: 1,
            barGap: 1,
            barRadius: 3,
            barHeight: 3,
            height: 25,
        });
        setWavesurfer(ws);

        ws.on('finish', () => {
            setIsPlaying(false);
        });

        return () => ws.destroy();
    }, []);

    const startRecording = () => {
        if (isRecording) {
            return;
        }
        navigator.mediaDevices.getUserMedia({ audio: true })
            .then(stream => {
                const mediaRecorder = new MediaRecorder(stream);
                mediaRecorderRef.current = mediaRecorder;
                audioChunksRef.current = [];
                setRecordingTime(0);

                mediaRecorder.ondataavailable = event => {
                    audioChunksRef.current.push(event.data);
                };
                mediaRecorder.onstop = () => {
                    const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
                    const audioUrl = URL.createObjectURL(audioBlob);
                    audioChunksRef.current = [];
                    if (wavesurfer) {
                        wavesurfer.load(audioUrl);
                    }
                    setHasRecording(true);
                };

                mediaRecorder.start();
                setIsRecording(true);

                intervalRef.current = setInterval(() => {
                    setRecordingTime(prevTime => {
                        if (prevTime >= 14) {
                            stopRecording();
                        }
                        return prevTime + 1;
                    });
                }, 1000);
            })
            .catch(error => {
                console.error('Error accessing media devices.', error);
            });
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            clearInterval(intervalRef.current);
        }
    };

    const onPlayPause = useCallback(() => {
        if (wavesurfer) {
            wavesurfer.playPause();
            setIsPlaying(!isPlaying);
        }
    }, [wavesurfer, isPlaying]);

    const formatTime = seconds => {
        let mins = Math.floor(seconds / 60);
        let secs = seconds % 60;
        return `${mins < 10 ? '0' + mins : mins}:${secs < 10 ? '0' + secs : secs}`;
    };

    const deleteRecording = () => {
        if (wavesurfer) {
            wavesurfer.stop();
            wavesurfer.empty();
        }
        setHasRecording(false);
        setRecordingTime(0);
        setIsPlaying(false);
    };

    return (
        <div className="audio-message">
          {isRecording && <span>{formatTime(recordingTime)}</span>}
          <div className="chat-items-picker-container chat-items-picker-container--inline">
              <Icon
                  iconName="microphone"
                  className={isRecording ? 'recording' : ''}
                  onClick={isRecording ? stopRecording : startRecording}
              />
          </div>
          <div className={`audio-message-bottom ${hasRecording ? 'visible' : 'hidden'}`}>
            <div className="track-wrapper">
              <div className="audio-controls">
                <Icon
                  iconName={isPlaying ? 'pause' : 'play'}
                  onClick={onPlayPause}
                />
              </div>
              <div ref={waveformRef} className="waveform-container"/>
            </div>
            <div className="delete-audio-message">
              <Icon
                iconName="trash"
                onClick={deleteRecording}
              />
            </div>
            <UpgradeLink href={CONFIG.urls.amupgrade} className="send-audio-message">
              <Icon iconName="sendPlane"/>
            </UpgradeLink>
          </div>
        </div>
    );
};

export default AudioMessage;
